import { Card } from '@/components/Elements'
import { UncontrolledSelect } from '@/components/UncontrolledForm'
import { ConfigType } from '@/features/configs/config.enum'
import useConfig from '@/features/configs/useConfig'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useContributorGroup } from '@/features/contributorGroups/useContributorGroup'
import { offerGCPRGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerGCPREditRight'
import { getStage } from '@/features/offers/helpers'
import usePrintCaptureMode from '@/features/offers/hooks/usePrintCaptureMode'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import useUser from '@/features/users/useUser'
import { authSelector } from '@/stores/auth/authSlice'
import { selectPrintCapture } from '@/stores/offers/printOfferModal'
import { useExchangeRate } from '@/utils/exchangeRate.helper'
import { paddingZeroString } from '@/utils/stringHelper'
import { Button, ButtonGroup, Flex } from '@chakra-ui/react'
import { BookingApproverRole, PRStatus } from '@eapproval/offer-shared'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import GCPRSubmissionTabs from './components/GCPRSubmissionTabs/GCPRSubmissionTabs'
import OfferGCMaterialProjectExecutionTable from './components/OfferGCMaterialProjectExecutionTable'
import {
  calculateGCPRSubmissionTotalAmount,
  getGCMaterialCurrency,
  getGCPRApproverDetails,
  getGCPRRowUnitCost,
} from './utils/GCPR.helper'

const OfferGCPRTab = () => {
  const [requestor, setRequestor] = useState('')
  const [selectedSubSystemIndex, setSelectedSubSystemIndex] = useState(null)
  const [GCPRSubmissionTabIndex, setGCPRSubmissionTabIndex] = useState(0)
  const [targetSubmissionIndex, setTargetSubmissionIndex] = useState(null)
  const [tabSearch, setTabSearch] = useState('')
  const [tabStatusFilter, setTabStatusFilter] = useState('')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const GCPRSubmissionIndex = searchParams.get('GCPRSubmissionIndex')

  const { user } = useSelector(authSelector)
  const offerPermissions = useSelector(selectOfferPermissions)
  const canEditGCPR = offerPermissions[offerGCPRGroupPolicyType.GCPR_EDIT]?.edit ?? false

  const isPrintCaptureMode = usePrintCaptureMode()
  // Print capture should always go through every submission, regardless of whatever filters
  // the user had active when they opened the print modal.
  const effectiveSelectedSubSystemIndex = isPrintCaptureMode ? null : selectedSubSystemIndex
  const effectiveTabSearch = isPrintCaptureMode ? '' : tabSearch
  const effectiveTabStatusFilter = isPrintCaptureMode ? '' : tabStatusFilter

  // The print modal drives GCPRSubmissionTabs' own nested tab strip, since GCPR's submissions
  // are a second, independent set of tabs nested inside this TabPanel.
  const { targetGCPRSubmissionIndex: printCaptureTargetGCPRSubmissionIndex } = useSelector(selectPrintCapture)

  const watchedGCMaterialSubmissions = useWatch({ name: `sourcing.GCMaterialSubmissions` })
  const GCMaterialSubmissions = useMemo(() => watchedGCMaterialSubmissions ?? [], [watchedGCMaterialSubmissions])

  const watchedProjectExecutionGCMaterialDetails = useWatch({ name: `sourcing.projectExecutionGCMaterialDetails` })
  const projectExecutionGCMaterialDetails = useMemo(
    () => watchedProjectExecutionGCMaterialDetails ?? [],
    [watchedProjectExecutionGCMaterialDetails]
  )
  const watchedEngineers = useWatch({ name: `engineers` })
  const engineers = useMemo(() => watchedEngineers ?? [], [watchedEngineers])
  const bookingProjectManager = useWatch({ name: `booking.projectManager` })
  const sales = useWatch({ name: 'createdBy' })

  const subSystems = useWatch({ name: 'subSystems' })
  const status = useWatch({ name: 'status' })
  const queryClient = useQueryClient()

  const { getValues, setValue } = useFormContext()

  const offerStage = getStage(status)

  const { append: appendGCPRSubmission } = useFieldArray({
    name: `sourcing.GCMaterialSubmissions`,
  })

  const { useFetchConfigByType, useFetchConfigDefaultUser } = useConfig()
  const { useFetchAllContributorGroups, useContributorGroupDefaultUser } = useContributorGroup()
  const { useFetchAllUsers } = useUser()

  const { data: defaultLogisticUser } = useFetchConfigDefaultUser(ConfigType.OFFER_GCPR_LOGISTIC_USER)
  const { data: defaultLicenseManagerUser } = useContributorGroupDefaultUser(ContributorGroupType.LICENSE_MANAGER)
  const { data: exchangeRateMap } = useExchangeRate()

  const { data: usersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    filters: { status: 'active' },
  })

  //Transformed

  const requestorOptions = useMemo(() => {
    return Array.isArray(usersResponse?.data)
      ? usersResponse.data
          .filter((user) => {
            return [...engineers, bookingProjectManager, sales].includes(user._id)
          })
          .map((user) => ({
            label: user.fullName,
            value: user._id,
          }))
      : []
  }, [usersResponse?.data, engineers, bookingProjectManager, sales])

  const filteredSubmissions = useMemo(() => {
    return [...GCMaterialSubmissions]
      .map((submission, originalIndex) => ({ submission, originalIndex }))
      .reverse()
      .filter(({ submission }) => {
        const q = effectiveTabSearch.toLowerCase()
        const matchesSearch =
          !effectiveTabSearch ||
          (submission.PRNumber ?? '').toLowerCase().includes(q) ||
          (submission.descriptionOfProduct ?? '').toLowerCase().includes(q)
        const matchesStatus = !effectiveTabStatusFilter || submission.status === effectiveTabStatusFilter
        const matchesSubSystem =
          effectiveSelectedSubSystemIndex === null ||
          (Array.isArray(submission.requestedDetails) &&
            submission.requestedDetails.some((d) => d.subSystemIndex === effectiveSelectedSubSystemIndex))
        return matchesSearch && matchesStatus && matchesSubSystem
      })
  }, [GCMaterialSubmissions, effectiveTabSearch, effectiveTabStatusFilter, effectiveSelectedSubSystemIndex])

  const currentTabEntry = filteredSubmissions[GCPRSubmissionTabIndex] ?? null

  useEffect(() => {
    if (requestor || !user?._id) {
      return
    }

    const isUserInRequestorOptions = requestorOptions.some((option) => option.value === user._id)

    if (isUserInRequestorOptions) {
      setRequestor(user._id)
    }
  }, [requestor, user?._id, requestorOptions])

  useEffect(() => {
    setGCPRSubmissionTabIndex(0)
  }, [tabSearch, tabStatusFilter])

  useEffect(() => {
    setGCPRSubmissionTabIndex((prevIndex) => {
      if (filteredSubmissions.length === 0) return 0
      return prevIndex >= filteredSubmissions.length ? filteredSubmissions.length - 1 : prevIndex
    })
  }, [filteredSubmissions.length])

  useEffect(() => {
    if (GCPRSubmissionIndex == null || filteredSubmissions.length === 0) return
    const targetOriginalIndex = Number(GCPRSubmissionIndex)
    const tabIndex = filteredSubmissions.findIndex(({ originalIndex }) => originalIndex === targetOriginalIndex)
    if (tabIndex !== -1) {
      setGCPRSubmissionTabIndex(tabIndex)
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('GCPRSubmissionIndex')
    navigate({ pathname: location.pathname, search: nextParams.toString() }, { replace: true })
  }, [GCPRSubmissionIndex, filteredSubmissions, searchParams, navigate, location.pathname])

  useEffect(() => {
    if (targetSubmissionIndex == null || filteredSubmissions.length === 0) return
    const tabIndex = filteredSubmissions.findIndex(({ originalIndex }) => originalIndex === targetSubmissionIndex)
    if (tabIndex !== -1) {
      setGCPRSubmissionTabIndex(tabIndex)
      setTargetSubmissionIndex(null)
    }
  }, [targetSubmissionIndex, filteredSubmissions])

  useEffect(() => {
    if (
      printCaptureTargetGCPRSubmissionIndex !== null &&
      printCaptureTargetGCPRSubmissionIndex !== GCPRSubmissionTabIndex
    ) {
      setGCPRSubmissionTabIndex(printCaptureTargetGCPRSubmissionIndex)
    }
  }, [printCaptureTargetGCPRSubmissionIndex, GCPRSubmissionTabIndex])

  const handleGeneratePR = async () => {
    if (!requestor) {
      toast.error('Requestor cannot be empty')
      return
    }

    const defaultExpectedDeliveryDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)

    const requestedDetails = projectExecutionGCMaterialDetails
      .filter((row) => row.newRequestedQuantity > 0)
      .map((row) => {
        const subSystem =
          Array.isArray(subSystems) && row.subSystemIndex < subSystems.length ? subSystems[row.subSystemIndex] : null

        const exchangeRate = exchangeRateMap?.[getGCMaterialCurrency(row)] ?? 1
        const manualUnitCostHKD = (row?.unitCost ?? 0) * exchangeRate

        const unitCostHKD = getGCPRRowUnitCost(row, exchangeRateMap)
        return {
          ...row,
          manualUnitCost: row?.unitCost ?? 0,
          manualUnitCostHKD: manualUnitCostHKD,
          unitCostHKD: unitCostHKD,
          detail: row.detail,
          purchaseScenario: row?.detail?.purchaseScenario,
          quantity: row.newRequestedQuantity,
          subSystemName: subSystem?.projectName ?? '',
          subSystemType: subSystem?.type,
          PONumber: '',
        }
      })

    if (requestedDetails.length === 0) {
      toast.error('Please fill at least 1 row')
      return
    }

    if (requestedDetails.some((detail) => detail.subSystemName === '')) {
      toast.error('Please make sure all items have valid Sub System selected')
      return
    }

    if (requestedDetails.some((detail) => detail.detail?.status === 'inactive')) {
      toast.error('Cannot generate PR for items with Inactive status')
      return
    }
    if (selectedSubSystemIndex == null) {
      toast.error('Please select a Sub System before generating a PR')
      return
    }

    const requestorDetail = usersResponse?.data.find((user) => user._id === requestor)
    if (!requestorDetail) {
      toast.error('Please select a valid requestor')
      return
    }

    const baseQuotationNumber = getValues(`baseQuotationNumber`)
    const totalAmount = calculateGCPRSubmissionTotalAmount(requestedDetails, exchangeRateMap)

    const approvers = getValues('booking.approverDetails')
    const projectManager = getValues('booking.projectManager')

    const commercialProjectManagerDetail = approvers.find(
      (detail) => detail.type === BookingApproverRole.COMMERCIAL_PROJECT_MANAGER && detail.level === 4
    )

    const licenseRequiredItems = requestedDetails.filter(
      (requestedDetail) => requestedDetail?.detail?.requiredLicenseManager
    )

    const nonLicenseRequiredItems = requestedDetails.filter(
      (requestedDetail) => !requestedDetail?.detail?.requiredLicenseManager
    )

    const currentSubmissionCount = GCMaterialSubmissions.length
    let generatedCount = 0

    if (licenseRequiredItems.length > 0) {
      const approverDetails = getGCPRApproverDetails({
        requestedDetails: licenseRequiredItems,
        defaultTPMUser: projectManager,
        defaultCPMUser: commercialProjectManagerDetail?.user?._id,
        defaultLicenseManagerUser: defaultLicenseManagerUser,
        defaultLogisticUser: defaultLogisticUser,
        requiredTPMApproval: user?._id !== projectManager,
      })

      appendGCPRSubmission({
        raiser: user,
        requestor: requestorDetail,
        PRNumber: `${baseQuotationNumber}-GC-PR${paddingZeroString(GCMaterialSubmissions.length + 1)}`,
        totalAmount: totalAmount,
        descriptionOfProduct: '',
        expectedDeliveryDate: defaultExpectedDeliveryDate,
        deliveryInstructions: '',
        status: PRStatus.DRAFT,
        subSystemIndex: selectedSubSystemIndex,
        requestedDetails: licenseRequiredItems,
        approverDetails: approverDetails,
        requestDate: new Date(),
      })
      generatedCount += 1
    }

    if (nonLicenseRequiredItems.length > 0) {
      const approverDetails = getGCPRApproverDetails({
        requestedDetails: nonLicenseRequiredItems,
        defaultTPMUser: projectManager,
        defaultCPMUser: commercialProjectManagerDetail?.user?._id,
        defaultLicenseManagerUser: defaultLicenseManagerUser,
        defaultLogisticUser: defaultLogisticUser,
        requiredTPMApproval: user?._id !== projectManager,
      })

      const PRNumber =
        licenseRequiredItems.length > 0
          ? `${baseQuotationNumber}-GC-PR${paddingZeroString(GCMaterialSubmissions.length + 2)}`
          : `${baseQuotationNumber}-GC-PR${paddingZeroString(GCMaterialSubmissions.length + 1)}`
      appendGCPRSubmission({
        raiser: user,
        requestor: requestorDetail,
        PRNumber: PRNumber,
        totalAmount: totalAmount,
        descriptionOfProduct: '',
        expectedDeliveryDate: defaultExpectedDeliveryDate,
        deliveryInstructions: '',
        status: PRStatus.DRAFT,
        subSystemIndex: selectedSubSystemIndex,
        requestedDetails: nonLicenseRequiredItems,
        approverDetails: approverDetails,
        requestDate: new Date(),
      })
      generatedCount += 1
    }

    if (generatedCount > 0) {
      setTargetSubmissionIndex(currentSubmissionCount + generatedCount - 1)
    }

    const currentDetails = getValues('sourcing.projectExecutionGCMaterialDetails') ?? []
    setValue(
      'sourcing.projectExecutionGCMaterialDetails',
      currentDetails.map((row) => ({ ...row, newRequestedQuantity: 0 })),
      { shouldDirty: true, shouldTouch: true }
    )
  }

  const handleAddToCurrentPR = async () => {
    if (!currentTabEntry) {
      toast.error('No PR selected')
      return
    }
    const { originalIndex, submission } = currentTabEntry
    if (submission.status !== PRStatus.DRAFT) {
      toast.error('Can only add items to a Draft PR')
      return
    }
    const newItems = projectExecutionGCMaterialDetails
      .filter((row) => row.newRequestedQuantity > 0)
      .map((row) => {
        const subSystem =
          Array.isArray(subSystems) && row.subSystemIndex < subSystems.length ? subSystems[row.subSystemIndex] : null
        const exchangeRate = exchangeRateMap?.[getGCMaterialCurrency(row)] ?? 1
        const manualUnitCostHKD = (row?.unitCost ?? 0) * exchangeRate
        const unitCostHKD = getGCPRRowUnitCost(row, exchangeRateMap)

        return {
          ...row,
          manualUnitCost: row?.unitCost ?? 0,
          manualUnitCostHKD: manualUnitCostHKD,
          detail: row.detail,
          unitCostHKD: unitCostHKD,
          purchaseScenario: row?.detail?.purchaseScenario,
          quantity: row.newRequestedQuantity,
          subSystemName: subSystem?.projectName,
          subSystemType: subSystem?.type,
          PONumber: '',
        }
      })

    if (newItems.length === 0) {
      toast.error('Please fill at least 1 row')
      return
    }

    //Validate if the added item has same  license manager requirement, if mixed, alert and return

    const currentPRRequiresLicenseManager = submission.requestedDetails.every(
      (item) => item.detail?.requiredLicenseManager
    )

    for (const newItem of newItems) {
      if (currentPRRequiresLicenseManager && !newItem?.detail?.requiredLicenseManager) {
        toast.error(
          `Current PR requires license manager. Item ${newItem?.modelNumber} does not meet the requirement. Please create a new PR for this item.`
        )
        return
      }
      if (!currentPRRequiresLicenseManager && newItem?.detail?.requiredLicenseManager) {
        toast.error(
          `Current PR does not require license manager. Item ${newItem?.modelNumber} does not meet the requirement. Please create a new PR for this item.`
        )
        return
      }
    }

    const currentRequestedDetails = getValues(`sourcing.GCMaterialSubmissions.${originalIndex}.requestedDetails`) ?? []

    const updatedDetails = [...currentRequestedDetails]
    let mergedCount = 0

    for (const newItem of newItems) {
      const key = `${newItem.subSystemIndex}__${newItem.modelNumber ?? ''}`
      const existingIdx = updatedDetails.findIndex(
        (item) => `${item.subSystemIndex}__${item.modelNumber ?? ''}` === key
      )
      if (existingIdx !== -1) {
        updatedDetails[existingIdx] = {
          ...updatedDetails[existingIdx],
          newRequestedQuantity:
            (updatedDetails[existingIdx].newRequestedQuantity ?? 0) + (newItem.newRequestedQuantity ?? 0),
          quantity: (updatedDetails[existingIdx].quantity ?? 0) + (newItem.quantity ?? 0),
        }
        mergedCount++
      } else {
        updatedDetails.push(newItem)
      }
    }

    setValue(`sourcing.GCMaterialSubmissions.${originalIndex}.requestedDetails`, updatedDetails, {
      shouldDirty: true,
      shouldTouch: true,
    })

    const updatedTotalAmount = calculateGCPRSubmissionTotalAmount(updatedDetails, exchangeRateMap)

    setValue(`sourcing.GCMaterialSubmissions.${originalIndex}.totalAmount`, updatedTotalAmount, {
      shouldDirty: true,
      shouldTouch: true,
    })
    const addedCount = newItems.length - mergedCount
    if (mergedCount > 0 && addedCount > 0) {
      toast.success(`${addedCount} item(s) added, ${mergedCount} item(s) quantity updated.`)
    } else if (mergedCount > 0) {
      toast.success(`${mergedCount} item(s) quantity updated.`)
    } else {
      toast.success('Items added to PR')
    }

    const currentDetails = getValues('sourcing.projectExecutionGCMaterialDetails') ?? []
    setValue(
      'sourcing.projectExecutionGCMaterialDetails',
      currentDetails.map((row) => ({ ...row, newRequestedQuantity: 0 })),
      { shouldDirty: true, shouldTouch: true }
    )
  }

  const handleDeleteDraft = (originalIndex) => {
    const currentSubmissions = getValues('sourcing.GCMaterialSubmissions') ?? []
    const updatedSubmissions = currentSubmissions.filter((_, submissionIndex) => submissionIndex !== originalIndex)

    setValue('sourcing.GCMaterialSubmissions', updatedSubmissions, {
      shouldDirty: true,
      shouldTouch: true,
    })

    setGCPRSubmissionTabIndex((prevIndex) => {
      const nextLength = updatedSubmissions.length
      if (nextLength === 0) return 0
      const displayIndexOfDeleted = currentSubmissions.length - 1 - originalIndex
      if (displayIndexOfDeleted < prevIndex) return prevIndex - 1
      if (prevIndex >= nextLength) return nextLength - 1
      return prevIndex
    })
  }

  return (
    <Flex gap="5" direction={'column'}>
      {!isPrintCaptureMode && Array.isArray(subSystems) && subSystems.length > 0 && (
        <ButtonGroup size="sm" flexWrap="wrap" gap="2">
          <Button
            colorScheme={selectedSubSystemIndex === null ? 'blue' : 'gray'}
            variant={selectedSubSystemIndex === null ? 'solid' : 'outline'}
            onClick={() => setSelectedSubSystemIndex(null)}
          >
            All
          </Button>
          {subSystems.map((subSystem, index) => (
            <Button
              key={index}
              colorScheme={selectedSubSystemIndex === index ? 'blue' : 'gray'}
              variant={selectedSubSystemIndex === index ? 'solid' : 'outline'}
              onClick={() => setSelectedSubSystemIndex(selectedSubSystemIndex === index ? null : index)}
            >
              {`1.${index + 1}`} {subSystem.projectName}
            </Button>
          ))}
        </ButtonGroup>
      )}
      <Card title={'Project Execution'} defaultIsOpen={true} canCollapse>
        <OfferGCMaterialProjectExecutionTable
          disabled={!canEditGCPR}
          subSystemFilter={effectiveSelectedSubSystemIndex}
        />

        {!isPrintCaptureMode && selectedSubSystemIndex !== null && (
          <Flex justifyContent={'flex-end'} gap="3" alignItems="center">
            <UncontrolledSelect
              label={'Requestor'}
              options={requestorOptions}
              disabled={!canEditGCPR}
              value={requestor}
              onChange={(option) => {
                if (option?.value) {
                  setRequestor(option.value)
                }
              }}
              width={'300px'}
              isClearable={false}
              isRequired={false}
            />
            <Button colorScheme="green" onClick={handleGeneratePR} isDisabled={!canEditGCPR || !requestor}>
              Generate PR
            </Button>
            <Button
              colorScheme="teal"
              onClick={handleAddToCurrentPR}
              isDisabled={!canEditGCPR || !currentTabEntry || currentTabEntry.submission.status !== PRStatus.DRAFT}
            >
              Add to current PR
            </Button>
          </Flex>
        )}
      </Card>
      <GCPRSubmissionTabs
        filteredSubmissions={filteredSubmissions}
        tabIndex={GCPRSubmissionTabIndex}
        onTabIndexChange={setGCPRSubmissionTabIndex}
        tabSearch={tabSearch}
        onTabSearchChange={setTabSearch}
        tabStatusFilter={tabStatusFilter}
        onTabStatusFilterChange={setTabStatusFilter}
        onDeleteDraft={handleDeleteDraft}
      />
    </Flex>
  )
}

export default OfferGCPRTab
